import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UserRole } from "reedy-data/api";
import { useSession } from "~/contexts/auth-context";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "~/components/ui/select";
import { toast } from "~/utils/toast";
import { useOrganisation } from "~/contexts/organisation-provider";
import { getUserRoleLabel, UserRoleLabel } from "./user-role-label";

interface UpdateOrganisationMemberUserRoleProps {
  userId: string;
  role: UserRole;
  email: string;

  showAdminRequiredModal(): void;
}

export const UpdateOrganisationMemberUserRole = ({
  userId,
  role,
  email,
  showAdminRequiredModal,
}: UpdateOrganisationMemberUserRoleProps) => {
  const { t } = useTranslation(["settings"]);

  const { user } = useSession();
  const { updateUserRole, members } = useOrganisation();

  const options = useMemo(() => {
    const roles = [UserRole.MEMBER, UserRole.ADMIN];

    if (user.isSuperAdmin) {
      roles.push(UserRole.SUPER_ADMIN);
    }

    return roles;
  }, [user.isSuperAdmin]);

  const onChange = async (value: UserRole) => {
    const admins = members.filter((m) => m.roles?.includes(UserRole.ADMIN));

    // If there's only 1 admin, and they're being changed to another role, display a warning
    if (
      admins.length === 1 &&
      admins[0].id === userId &&
      value !== UserRole.ADMIN
    ) {
      showAdminRequiredModal();
      return;
    }

    try {
      await updateUserRole(userId, value);

      toast.success(
        t("settings:organisation.members.role.update.success", {
          email,
          role: getUserRoleLabel(value, t),
        }),
      );
    } catch {
      toast.error(
        t("settings:organisation.members.role.update.error", {
          email,
          role: getUserRoleLabel(value, t),
        }),
      );
    }
  };

  if (user.id === userId) {
    return null;
  }

  return (
    <Select value={role} onValueChange={(value) => onChange(value as UserRole)}>
      <SelectTrigger className="w-35" data-testid="user-role-options">
        <UserRoleLabel role={role} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              <UserRoleLabel role={option} />
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};
